import { titleCase } from 'change-case'
import { DragEventHandler, Fragment, MouseEventHandler, useState } from 'react'
import {
  Button,
  kuiperClassNames,
  renderIcon,
  WithTooltip,
} from '@opallabs/kuiper'
import { useModuleSectionDataFormatter } from '../../Content/helpers/hooks'
import {
  GetSectionPostOptionsProps,
  OnDragDropProps,
  OnShowAssetPickerProps,
} from '../../Content/helpers/types'
import type {
  Module,
  ModuleDefinition,
  SectionPostOptions,
} from '../../Content/helpers/types'
import { ModularSectionOptions } from '../../ModularSectionOptions'
import type { SectionOptionProps } from '../../ModularSectionOptions/types'
import { styles } from './styles'

const onDragOver: DragEventHandler = (e) => e.preventDefault()

interface Props {
  allProps: Record<string, unknown>
  availableModules: ModuleDefinition[]
  disableSectionOptions?: boolean
  getModule?: (args: GetSectionPostOptionsProps) => SectionPostOptions
  index: number
  isDragging?: boolean
  isExpanded?: boolean
  isPresenting: boolean
  module: Module
  modules: Module[]
  onDeleteSection: (index: number) => void
  onDragDrop: OnDragDropProps
  onDragEnter?: MouseEventHandler
  onDragStart?: (e: React.DragEvent<HTMLDivElement>) => void
  onExpandAsset: (assetIndex: number) => void
  onRemoveAsset: (assetIndex: number, index: number) => void
  onShowAssetPicker: (args: OnShowAssetPickerProps) => void
  onTooltipToggle: (index: number) => void
  readOnly: boolean
  tooltipActive: boolean
  updateSectionOptions: (index: number, value: string, name: string) => void
  width: number
}

export const CarouselSection: React.FC<Props> = ({
  allProps,
  availableModules,
  disableSectionOptions,
  getModule,
  index,
  isDragging,
  isExpanded,
  isPresenting,
  module,
  modules,
  onDeleteSection,
  onDragDrop,
  onDragEnter,
  onDragStart,
  onExpandAsset,
  onRemoveAsset,
  onShowAssetPicker,
  onTooltipToggle,
  readOnly,
  tooltipActive,
  updateSectionOptions,
  width,
}) => {
  const { assets, content, sectionPostOptions, sectionOptions } =
    useModuleSectionDataFormatter(module)
  const [showSectionOptions, setShowSectionOptions] = useState(
    !disableSectionOptions && (!isExpanded || tooltipActive)
  )

  const mappedComponent =
    module &&
    availableModules.find(
      (mod) =>
        mod.componentName === module.componentName ||
        mod.componentName === module.name
    )
  const Component = mappedComponent?.component
  const moduleSectionOptions = getModule
    ? getModule({ sectionPostOptions, module })
    : {}

  const deleteSection = (index: number): void => {
    onDeleteSection(index)
  }

  const saveSectionOptions = (options: SectionOptionProps[]): void => {
    onTooltipToggle(-1)
    onSectionOptionsSave(options)
  }

  const onSectionOptionsSave = (options: SectionOptionProps[]) => {
    const desiredIndex =
      options[0] && options[0].selected
        ? +options[0].selected?.title - 1
        : index
    options.forEach((option) => {
      const optionName = option.option.toLowerCase()
      if (optionName === 'section position') {
        updateSectionOptions(index, desiredIndex.toString(), optionName)
      } else if (option.type === 'select') {
        updateSectionOptions(
          desiredIndex,
          option.selected?.title || '',
          optionName
        )
      } else {
        updateSectionOptions(
          desiredIndex,
          (option.content || '') as string,
          optionName
        )
      }
    })
  }

  return (
    <Fragment>
      {Component && mappedComponent && (
        <div
          css={styles.moduleWrapperStyle}
          onMouseEnter={() => setShowSectionOptions(true)}
          onMouseLeave={() =>
            setShowSectionOptions(!isExpanded || tooltipActive)
          }
          onDragEnter={onDragEnter}
          data-zone-id={index}
          onDragOver={onDragOver}
        >
          <div css={styles.moduleWrapperStyle}>
            <Component
              assets={assets || []}
              content={content}
              index={index}
              modules={modules}
              readOnly={readOnly}
              isDragging={isDragging}
              isPresenting={isPresenting}
              isExpanded={isExpanded}
              onDragDrop={onDragDrop}
              onExpandAsset={onExpandAsset}
              onRemoveAsset={(assetId: number) => onRemoveAsset(assetId, index)}
              onShowAssetPicker={(args: OnShowAssetPickerProps) =>
                onShowAssetPicker({ ...args, index })
              }
              postType={(allProps.postType as string) || ''}
              updateText={(value: string, name: string) =>
                updateSectionOptions(
                  index,
                  value,
                  titleCase(name).toLocaleLowerCase()
                )
              }
              {...allProps}
              {...moduleSectionOptions}
            />
          </div>
          {!readOnly && !isPresenting && (
            <div
              className={kuiperClassNames('carousel-section-sidebar-options')}
              css={styles.getSidebarWrapperStyle(
                width,
                isExpanded,
                showSectionOptions
              )}
            >
              {isExpanded && (
                <WithTooltip
                  tooltipOptions={{ position: 'left' }}
                  content="Drag to Reorder"
                >
                  <div
                    css={styles.getSidebarButton(isDragging || false)}
                    className={kuiperClassNames(
                      'carousel-section-options-popover'
                    )}
                    draggable={true}
                    onDragStart={onDragStart}
                  >
                    <div css={styles.getOptionsStyling(isDragging || false)}>
                      {renderIcon('grabber-nsew', {
                        ariaLabel: 'Drag to Reorder Section Icon Trigger',
                      })}
                    </div>
                  </div>
                </WithTooltip>
              )}
              {!isDragging && !disableSectionOptions && (
                <WithTooltip
                  tooltipOptions={{ position: 'left' }}
                  content="Section Options"
                  disabled={tooltipActive}
                >
                  <div
                    css={styles.getSidebarButton(false)}
                    className={kuiperClassNames(
                      'carousel-section-options-popover'
                    )}
                  >
                    {tooltipActive && (
                      <div
                        className={kuiperClassNames(
                          'carousel-section-options-popover-body'
                        )}
                        css={styles.getPopoverStyling(
                          tooltipActive,
                          isExpanded
                        )}
                      >
                        <ModularSectionOptions
                          componentName={mappedComponent.title}
                          deleteDisabled={modules.length <= 1}
                          index={index}
                          onExit={() => onTooltipToggle(-1)}
                          onSectionDelete={() => deleteSection(index)}
                          onSectionOptionsSave={(
                            options: SectionOptionProps[]
                          ) => saveSectionOptions(options)}
                          readOnly={readOnly}
                          timezone={allProps.timezone as string}
                          sectionOptions={sectionOptions}
                          sections={modules}
                        />
                      </div>
                    )}
                    <span css={styles.getOptionsStyling(false)}>
                      <Button
                        aria-label="Toggle Section Options Popover Button"
                        icon="cog-lg"
                        iconOptions={{ ariaMode: 'hidden' }}
                        type="nobutton"
                        onClick={() => onTooltipToggle(index)}
                      />
                    </span>
                  </div>
                </WithTooltip>
              )}
            </div>
          )}
        </div>
      )}
    </Fragment>
  )
}
